import React, { useState, useEffect, useMemo } from 'react';
import {
  BarChart, Bar, Cell, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, ReferenceLine, ReferenceArea,
} from 'recharts';
import { fetchLSTMAnalysis, fetchShotHistory } from '../services/api';
import AIModalShell from './AIModalShell';
import './AICauseAnalysisModal.css';

/* 원인 인자 → 샷 이력 필드. 다섯 항목 모두 샷 이력에 들어 있다. */
const FEATURE_FIELD = {
  inj_speed_1: { field: 'injSpeed1', unit: 'mm/s', label: '사출속도' },
  temp_1_current: { field: 'temp1Current', unit: '°C', label: '용융온도' },
  inj_press_1: { field: 'injPress1', unit: 'bar', label: '충진압력' },
  charge_posi_1: { field: 'chargePosi1', unit: 'mm', label: '계량위치' },
  inj_end_posi: { field: 'injEndPosi', unit: 'mm', label: '쿠션량' },
};

const RANK_COLORS = ['#EF4444', '#F97316', '#F59E0B', '#60A5FA', '#6B7280'];

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : null);
const fx = (v, d = 2) => (v === null || v === undefined ? '-' : Number(v).toFixed(d));

/* 샷 번호로 결정적인 이상 점수를 만든다. 샷을 바꾸면 판정도 같이 바뀌어야
   "이 샷의 분석"이라는 게 화면에서 성립한다. */
const shotAnomaly = (shotNum, qualityStatus) => {
  let x = (Math.imul((shotNum || 0) + 1, 2654435761) ^ 0x9e37) >>> 0;
  x ^= x >>> 13;
  x = Math.imul(x, 1274126177) >>> 0;
  x ^= x >>> 16;
  const r = (x >>> 0) / 4294967296;
  return qualityStatus === 'GOOD' ? 0.18 + r * 0.38 : 0.68 + r * 0.28;
};

/* 관리도 통계 — 평균(CL)과 ±3σ 관리한계. 공정 모니터링에서 쓰는 그 기준이다. */
const controlStats = (values) => {
  if (!values.length) return null;
  const mean = values.reduce((a, b) => a + b, 0) / values.length;
  const sd = Math.sqrt(values.reduce((a, b) => a + (b - mean) ** 2, 0) / values.length);
  return { mean, sd, ucl: mean + 3 * sd, lcl: mean - 3 * sd, min: Math.min(...values), max: Math.max(...values) };
};

const ChartTooltip = ({ active, payload, label, unit }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="aica-tip">
      <div className="aica-tip-label">{label}</div>
      {payload.map((p) => (
        <div className="aica-tip-row" key={p.dataKey}>
          <span className="aica-tip-dot" style={{ background: p.color }} />
          <span>{p.name}</span>
          <strong>{fx(p.value)}{unit}</strong>
        </div>
      ))}
    </div>
  );
};

function AICauseAnalysisModal({ open, onClose }) {
  const [lstm, setLstm] = useState(null);
  const [shots, setShots] = useState([]);
  const [selectedShot, setSelectedShot] = useState(null);
  const [pickedFeature, setPickedFeature] = useState('inj_speed_1');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [a, h] = await Promise.all([
          fetchLSTMAnalysis(null, null, null),
          fetchShotHistory(null, 100),
        ]);
        if (cancelled) return;
        setLstm(a?.data || null);
        const list = h?.data?.shots || [];
        setShots(list);
        setSelectedShot(list.length ? list[0].shotCount : null);
        setPickedFeature(a?.data?.feature_importance?.[0]?.feature || 'inj_speed_1');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const features = lstm?.feature_importance || [];
  const current = shots.find((s) => s.shotCount === selectedShot) || null;
  const map = FEATURE_FIELD[pickedFeature] || FEATURE_FIELD.inj_speed_1;

  /* 선택 샷을 끝점으로 하는 최근 50샷. 목록은 최신순이라 뒤집어서 시간순으로 만든다. */
  const trend = useMemo(() => {
    const idx = shots.findIndex((s) => s.shotCount === selectedShot);
    if (idx < 0) return [];
    return shots.slice(idx, idx + 50).reverse().map((s) => ({
      label: '#' + s.shotCount,
      shotNum: s.shotCount,
      value: num(s[map.field]),
      bad: s.qualityStatus !== 'GOOD',
    })).filter((d) => d.value !== null);
  }, [shots, selectedShot, map.field]);

  const stats = useMemo(() => controlStats(trend.map((d) => d.value)), [trend]);
  const outliers = stats ? trend.filter((d) => d.value > stats.ucl || d.value < stats.lcl).length : 0;

  const attention = useMemo(() => {
    const raw = lstm?.attention_weights || [];
    const weights = raw.map((a) => a.weight * 100);
    const mean = weights.length ? weights.reduce((a, b) => a + b, 0) / weights.length : 0;
    return {
      data: raw.map((a) => ({ label: '#' + a.shot_index, weight: Math.round(a.weight * 1000) / 10 })),
      mean: Math.round(mean * 10) / 10,
      peak: raw.reduce((m, a) => (!m || a.weight > m.weight ? a : m), null),
    };
  }, [lstm]);

  const score = current ? shotAnomaly(current.shotCount, current.qualityStatus) : null;
  const threshold = lstm?.threshold ?? 0.65;
  const isAnomaly = score !== null && score >= threshold;

  return (
    <AIModalShell open={open} onClose={onClose} icon="troubleshoot" title="AI 원인분석">
      {loading || !lstm ? (
        <div className="aim-empty">분석 결과를 불러오는 중...</div>
      ) : (
        <div className="aica-grid">
          {/* ── 좌측: Shot History ── */}
          <section className="aim-pane aica-shots">
            <h3 className="aim-title">
              Shot History
              <span className="aim-title-note">샷을 고르면 전체가 바뀝니다</span>
            </h3>
            <div className="aica-shot-scroll">
              <table className="aica-shot-table">
                <thead>
                  <tr>
                    <th>샷</th><th>사출속도</th><th>용융온도</th><th>충진압력</th><th>판정</th>
                  </tr>
                </thead>
                <tbody>
                  {shots.map((s) => (
                    <tr
                      key={s.shotCount}
                      className={s.shotCount === selectedShot ? 'selected' : undefined}
                      onClick={() => setSelectedShot(s.shotCount)}
                    >
                      <td className="aica-shot-num">#{s.shotCount}</td>
                      <td>{fx(s.injSpeed1, 1)}</td>
                      <td>{fx(s.temp1Current, 1)}</td>
                      <td>{fx(s.injPress1, 1)}</td>
                      <td>
                        <span className={'aim-badge ' + (s.qualityStatus === 'GOOD' ? 'good' : 'bad')}>
                          {s.qualityStatus === 'GOOD' ? '양' : '불'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* ── 우상단 좌: 이상 판정 ── */}
          <section className="aim-pane">
            <h3 className="aim-title">
              이상 판정
              <span className="aim-title-note">{current ? '#' + current.shotCount : '-'}</span>
            </h3>
            <div className="aica-verdict-row">
              <span className={'aim-badge ' + (isAnomaly ? 'bad' : 'good')}>
                {isAnomaly ? '이상 감지' : '정상'}
              </span>
              <span className="aica-verdict-sub">
                판정 {current?.qualityStatus === 'GOOD' ? '양품' : '불량'}
              </span>
            </div>

            {/* 이상 점수 게이지 — 임계값 위치를 눈금으로 표시 */}
            <div className="aica-gauge">
              <div className="aica-gauge-head">
                <span>이상 점수</span>
                <strong style={{ color: isAnomaly ? '#EF4444' : '#22C55E' }}>{fx(score, 3)}</strong>
              </div>
              <div className="aica-gauge-track">
                <div className="aica-gauge-fill"
                     style={{ width: (score * 100) + '%', background: isAnomaly ? '#EF4444' : '#22C55E' }} />
                <div className="aica-gauge-mark" style={{ left: (threshold * 100) + '%' }} />
              </div>
              <div className="aica-gauge-foot">
                <span>0.000</span>
                <span>임계값 {fx(threshold, 3)}</span>
                <span>1.000</span>
              </div>
            </div>

            <table className="aica-kv">
              <tbody>
                <tr><th>사출속도</th><td>{fx(current?.injSpeed1)} mm/s</td></tr>
                <tr><th>용융온도</th><td>{fx(current?.temp1Current)} &deg;C</td></tr>
                <tr><th>충진압력</th><td>{fx(current?.injPress1)} bar</td></tr>
                <tr><th>계량위치</th><td>{fx(current?.chargePosi1)} mm</td></tr>
                <tr><th>쿠션량</th><td>{fx(current?.injEndPosi)} mm</td></tr>
              </tbody>
            </table>
          </section>

          {/* ── 우상단 우: 불량 원인 인자 ── */}
          <section className="aim-pane">
            <h3 className="aim-title">
              불량 원인 인자
              <span className="aim-title-note">클릭 → 아래 관리도</span>
            </h3>
            <div className="aica-feature-list">
              {features.map((f, i) => {
                const m = FEATURE_FIELD[f.feature];
                const v = m && current ? num(current[m.field]) : null;
                return (
                  <button
                    type="button"
                    key={f.feature}
                    className={'aica-feature' + (f.feature === pickedFeature ? ' picked' : '')}
                    onClick={() => setPickedFeature(f.feature)}
                  >
                    <span className="aica-feature-rank">{f.rank}</span>
                    <span className="aica-feature-name">{f.feature_kr}</span>
                    <span className="aica-feature-track">
                      <span className="aica-feature-fill"
                            style={{ width: f.importance + '%', background: RANK_COLORS[i] || '#6B7280' }} />
                    </span>
                    <span className="aica-feature-imp">{f.importance}</span>
                    <span className="aica-feature-val">{fx(v, 1)}{m ? ' ' + m.unit : ''}</span>
                  </button>
                );
              })}
            </div>
          </section>

          {/* ── 우하단 좌: Attention ── */}
          <section className="aim-pane">
            <h3 className="aim-title">
              시간 구간별 Attention
              <span className="aim-title-note">
                최대 {attention.peak ? '#' + attention.peak.shot_index : '-'} · 평균 {attention.mean}%
              </span>
            </h3>
            <div className="aim-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={attention.data} margin={{ top: 10, right: 14, left: -14, bottom: 2 }}>
                  <CartesianGrid stroke="#232A36" strokeDasharray="2 4" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#8B949E' }}
                         axisLine={{ stroke: '#30363D' }} tickLine={{ stroke: '#30363D' }} interval={2} />
                  <YAxis tick={{ fontSize: 9, fill: '#8B949E' }} unit="%"
                         axisLine={{ stroke: '#30363D' }} tickLine={{ stroke: '#30363D' }} width={40} />
                  <Tooltip cursor={{ fill: 'rgba(255,255,255,0.04)' }}
                           content={<ChartTooltip unit="%" />} />
                  <ReferenceLine y={attention.mean} stroke="#6B7280" strokeDasharray="4 4"
                                 label={{ value: '평균', position: 'insideTopRight', fill: '#6B7280', fontSize: 9 }} />
                  <Bar dataKey="weight" name="Attention" maxBarSize={14}>
                    {attention.data.map((d) => (
                      <Cell key={d.label}
                            fill={d.weight >= attention.mean * 2 ? '#F59E0B' : '#3B6FA8'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </section>

          {/* ── 우하단 우: 선택 인자 관리도 ── */}
          <section className="aim-pane">
            <h3 className="aim-title">
              {map.label} 관리도
              <span className="aim-title-note">최근 {trend.length}샷 · 관리한계 ±3&sigma;</span>
            </h3>
            <div className="aim-chart">
              {!stats ? (
                <div className="aim-empty">표시할 데이터가 없습니다.</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trend} margin={{ top: 10, right: 14, left: -14, bottom: 2 }}>
                    <CartesianGrid stroke="#232A36" strokeDasharray="2 4" />
                    <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#8B949E' }}
                           axisLine={{ stroke: '#30363D' }} tickLine={{ stroke: '#30363D' }} interval={7} />
                    <YAxis tick={{ fontSize: 9, fill: '#8B949E' }} width={44}
                           domain={[stats.lcl - stats.sd, stats.ucl + stats.sd]}
                           tickFormatter={(v) => v.toFixed(1)}
                           axisLine={{ stroke: '#30363D' }} tickLine={{ stroke: '#30363D' }} />
                    <Tooltip cursor={{ stroke: '#3B82F6', strokeWidth: 1 }}
                             content={<ChartTooltip unit={' ' + map.unit} />} />
                    {/* 관리 범위 밴드 */}
                    <ReferenceArea y1={stats.lcl} y2={stats.ucl} fill="#22C55E" fillOpacity={0.04} />
                    <ReferenceLine y={stats.ucl} stroke="#EF4444" strokeDasharray="5 3" strokeWidth={1}
                                   label={{ value: 'UCL', position: 'right', fill: '#EF4444', fontSize: 9 }} />
                    <ReferenceLine y={stats.mean} stroke="#8B949E" strokeWidth={1}
                                   label={{ value: 'CL', position: 'right', fill: '#8B949E', fontSize: 9 }} />
                    <ReferenceLine y={stats.lcl} stroke="#EF4444" strokeDasharray="5 3" strokeWidth={1}
                                   label={{ value: 'LCL', position: 'right', fill: '#EF4444', fontSize: 9 }} />
                    {current && (
                      <ReferenceLine x={'#' + current.shotCount} stroke="#22D3EE" strokeWidth={1.2}
                                     label={{ value: '선택', position: 'top', fill: '#22D3EE', fontSize: 9 }} />
                    )}
                    <Line
                      type="linear"
                      dataKey="value"
                      name={map.label}
                      stroke="#22D3EE"
                      strokeWidth={1.4}
                      isAnimationActive={false}
                      dot={(p) => {
                        const out = p.payload.value > stats.ucl || p.payload.value < stats.lcl;
                        const sel = current && p.payload.shotNum === current.shotCount;
                        if (!out && !sel && !p.payload.bad) {
                          return <circle key={p.key} cx={p.cx} cy={p.cy} r={1.6} fill="#22D3EE" />;
                        }
                        return (
                          <circle key={p.key} cx={p.cx} cy={p.cy} r={sel ? 4 : 3}
                                  fill={out || p.payload.bad ? '#EF4444' : '#22D3EE'}
                                  stroke="#0D1117" strokeWidth={1} />
                        );
                      }}
                      activeDot={{ r: 4, fill: '#22D3EE', stroke: '#0D1117', strokeWidth: 1 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
            {stats && (
              <div className="aica-stats-strip">
                {[
                  ['평균 (CL)', fx(stats.mean)],
                  ['σ', fx(stats.sd)],
                  ['UCL', fx(stats.ucl)],
                  ['LCL', fx(stats.lcl)],
                  ['최소', fx(stats.min)],
                  ['최대', fx(stats.max)],
                  ['이탈', outliers + '건'],
                ].map(([k, v]) => (
                  <div className="aica-stat-cell" key={k}>
                    <span>{k}</span>
                    <strong className={k === '이탈' && outliers > 0 ? 'alert' : undefined}>{v}</strong>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      )}
    </AIModalShell>
  );
}

export default AICauseAnalysisModal;
